import type {
  CabinetAppointmentLookup,
  CabinetAppointmentView,
  CabinetCancelAppointmentRequest,
  CabinetCreateAppointmentRequest,
  CabinetRescheduleAppointmentRequest,
  CabinetUpdateAppointmentRequest,
  CreateBusyBlockRequest,
  CreateManualHoldRequest,
  DeleteBusyBlockRequest,
  MarkAppointmentArrivedRequest,
  MarkAppointmentCompletedRequest,
  MarkAppointmentNoShowRequest,
  SlotHoldView,
  UndoAppointmentNoShowRequest,
} from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"

/**
 * Действия журнала: всё, что МЕНЯЕТ день.
 *
 * Чтение дня живёт в journal.api.ts и остаётся отдельным файлом намеренно: чтение
 * повторяется само (перелистывание, обновление после правки), а мутация повторяется
 * только по решению человека и только с ключом идемпотентности.
 *
 * ПОЧЕМУ КЛЮЧ — АРГУМЕНТ, А НЕ ВНУТРЕННЕЕ ДЕЛО ЭТИХ ФУНКЦИЙ. Ключ идемпотентности
 * обязан пережить повтор: администратор нажал «Записать», ответ не доехал, он нажимает
 * снова — и обязан получить ТУ ЖЕ запись, а не вторую. Ключ, рождённый внутри вызова,
 * был бы новым у каждой попытки, то есть ровно бесполезным. Поэтому ключ приходит
 * сверху, из книги ключей (journal/idempotency-book.ts), которая помнит намерение
 * человека, а не сетевую попытку.
 *
 * ПОЧЕМУ ОТВЕТЫ НЕ ПРОВЕРЯЮТСЯ СХЕМОЙ. По той же причине, что и в journal.api.ts: типы
 * настоящие, из контрактов, а разбор Zod на каждое нажатие «Клиент пришёл» стоит больше,
 * чем ловит. Отказы при этом разбираются всегда — их разбирает api/problem.ts.
 */

/** Создание записи (быстрая форма 2-2.7.1). 201, ответ — созданная запись. */
export function createAppointment(
  request: CabinetCreateAppointmentRequest,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>("/cabinet/appointments", {
    method: "POST",
    body: request,
    idempotencyKey,
    signal,
  })
}

/** Правка состава визита. Право appointments.past.edit. */
export function updateAppointment(
  request: CabinetUpdateAppointmentRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(`/cabinet/appointments/${request.appointmentId}`, {
    method: "PATCH",
    body: request,
    idempotencyKey,
  })
}

/**
 * «Подтвердить» онлайн-запись, которая ждёт подтверждения салоном: pending → confirmed.
 * Раньше этого перехода в кабинете не было вовсе, и салон с ручным подтверждением
 * не мог подтвердить ни одну запись.
 */
export function confirmAppointment(
  request: MarkAppointmentArrivedRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/confirm`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/** «Клиент пришёл» — отметка факта, подтверждения не требует (ТЗ §2.9.1). */
export function markAppointmentArrived(
  request: MarkAppointmentArrivedRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/arrived`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/** «Завершено». Итоговая сумма передаётся только если она разошлась с планом. */
export function markAppointmentCompleted(
  request: MarkAppointmentCompletedRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/completed`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/**
 * «Не пришёл» (2-2.9.3). Блок blacklist дополнительно требует права
 * clients.blacklist.manage — оно проверяется в сервисе, а не стражем маршрута,
 * поэтому кабинет не имеет права показывать отметку «Ограничить онлайн-запись»
 * тому, у кого этого права нет: иначе отказ придёт уже после нажатия.
 */
export function markAppointmentNoShow(
  request: MarkAppointmentNoShowRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/no-show`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/** Снятие неявки. Существует ровно потому, что администраторы ошибаются. */
export function undoAppointmentNoShow(
  request: UndoAppointmentNoShowRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/no-show/undo`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/** Перенос. Не «отмена плюс создание»: история записи обязана остаться одной нитью. */
export function rescheduleAppointment(
  request: CabinetRescheduleAppointmentRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/reschedule`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/** Отмена (2-2.9.2). initiatedBy и reasonCode обязательны — от них зависят последствия. */
export function cancelAppointment(
  request: CabinetCancelAppointmentRequest,
  idempotencyKey: string,
): Promise<CabinetAppointmentView> {
  return apiRequest<CabinetAppointmentView>(
    `/cabinet/appointments/${request.appointmentId}/cancel`,
    { method: "POST", body: request, idempotencyKey },
  )
}

/**
 * Удержание слота на время оформления («Держим ещё 4:12»).
 *
 * Отсчёт считает кабинет от expiresAt, а не от «осталось N секунд»: секунды протухают
 * в пути и в спящем ноутбуке. Право holds.manage есть не у всех — без него форма просто
 * работает без удержания, и это честнее, чем показывать отсчёт, которого нет на сервере.
 */
export function createSlotHold(
  request: CreateManualHoldRequest,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<SlotHoldView> {
  return apiRequest<SlotHoldView>("/cabinet/holds", {
    method: "POST",
    body: request,
    idempotencyKey,
    signal,
  })
}

/**
 * Снять СВОЁ удержание. Ключ идемпотентности здесь не нужен и не посылается:
 * маршрут его не требует (cabinet-busy.controller.ts), а повторное снятие уже снятого
 * удержания и так ничего не ломает.
 */
export function releaseSlotHold(holdId: string): Promise<void> {
  return apiRequest<void>(`/cabinet/holds/${holdId}`, { method: "DELETE" })
}

/**
 * Одна запись по номеру, без знания даты (1-Э1.14).
 *
 * Нужна ссылкам: `?appointment=<id>` присылают в поддержку и в переписку, а журнал
 * грузит ОДИН день — вчерашняя запись в нём не находится. Ответ несёт календарную дату
 * филиала, по которой журнал открывает нужный день, и название филиала для отказа
 * «эта запись в другом филиале».
 */
export function fetchAppointmentById(
  appointmentId: string,
  signal?: AbortSignal,
): Promise<CabinetAppointmentLookup> {
  return apiRequest<CabinetAppointmentLookup>(`/cabinet/appointments/${appointmentId}`, { signal })
}

/**
 * Заблокировать время («Заблокировать время» из карточки и «Перерыв» из сетки).
 *
 * Ключ идемпотентности ОБЯЗАТЕЛЕН: двойное нажатие на телефоне — не редкость, а норма,
 * и второй блок поверх первого сервер отверг бы конфликтом занятости, показав человеку
 * «это время уже занято» на действие, которое он только что сам и выполнил.
 *
 * Предупреждения (запись внутри блокируемого окна, вне смены) сервер возвращает 422
 * с confirmation_required — их подтверждает человек, а кабинет повторяет запрос
 * с теми же кодами в acknowledgedWarnings.
 */
export function createBusyBlock(
  request: CreateBusyBlockRequest,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<{ readonly id: string }> {
  return apiRequest<{ readonly id: string }>("/cabinet/busy", {
    method: "POST",
    body: request,
    idempotencyKey,
    signal,
  })
}

/**
 * Снять блокировку. Версия строки обязательна: перерыв, который сосед только что
 * подвинул, иначе исчезнет молча — «последний победил» в общем расписании недопустим.
 */
export function deleteBusyBlock(
  request: DeleteBusyBlockRequest,
  idempotencyKey: string,
): Promise<void> {
  return apiRequest<void>(`/cabinet/busy/${request.busyId}`, {
    method: "DELETE",
    body: request,
    idempotencyKey,
  })
}

// ───────────────────────── услуги для быстрой формы ─────────────────────────

/**
 * Услуга, которую можно выбрать в быстрой форме.
 *
 * ПОЧЕМУ ТИП ОПИСАН ЗДЕСЬ, А НЕ ВЗЯТ ИЗ КОНТРАКТОВ. GET /v1/cabinet/catalog отдаёт
 * строки базы как есть — `Record<string, unknown>` в snake_case
 * (apps/api/src/modules/cabinet/directory.repository.ts, listServices). Контракта
 * на этот ответ в packages/contracts нет вовсе. Поэтому здесь описано ровно то,
 * что нужно форме, а разбор ниже принимает ОБА написания: когда у ответа появится
 * контракт с camelCase, форма переживёт это без правок.
 */
export interface BookableService {
  readonly id: string
  readonly name: string
  readonly durationMinutes: number
  /** null — деньги этому человеку не показываются, а не «услуга бесплатная». */
  readonly priceMinor: number | null
  readonly isActive: boolean
}

/** Мастер с перечнем услуг, которые он делает. Нужен ради «Только услуги Анны». */
export interface ServiceEmployee {
  readonly id: string
  readonly displayName: string
  readonly serviceIds: readonly string[]
}

export interface BookingCatalog {
  readonly services: readonly BookableService[]
  readonly employees: readonly ServiceEmployee[]
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Первое из двух написаний имени поля. Ответ каталога сегодня в snake_case. */
function pick(row: Record<string, unknown>, camel: string, snake: string): unknown {
  const value = row[camel]
  return value === undefined ? row[snake] : value
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null
}

function asCount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  // Денежные и большие целые приходят из PostgreSQL строкой: bigint в JSON не влезает.
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value)
  }
  return null
}

function asFlag(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value
  if (value === "true" || value === "t") return true
  if (value === "false" || value === "f") return false
  return fallback
}

function toService(raw: unknown): BookableService | null {
  const row = asRecord(raw)
  if (row === null) return null
  const id = asText(row["id"])
  const name = asText(row["name"])
  const duration = asCount(pick(row, "durationMinutes", "duration_minutes"))
  if (id === null || name === null || duration === null || duration <= 0) return null
  return {
    id,
    name,
    durationMinutes: duration,
    priceMinor: asCount(pick(row, "priceMinor", "price_minor")),
    isActive: asFlag(pick(row, "isActive", "is_active"), true),
  }
}

function toEmployee(raw: unknown): ServiceEmployee | null {
  const row = asRecord(raw)
  if (row === null) return null
  const id = asText(row["id"])
  const displayName = asText(pick(row, "displayName", "display_name"))
  if (id === null || displayName === null) return null
  const services = pick(row, "serviceIds", "service_ids")
  return {
    id,
    displayName,
    serviceIds: Array.isArray(services)
      ? services.filter((item): item is string => typeof item === "string")
      : [],
  }
}

/**
 * Услуги и их исполнители одним обращением.
 *
 * Два запроса, а не один, потому что маршрута «услуги этого мастера» в API нет:
 * перечень услуг лежит в каталоге, а связка «кто что делает» — в списке сотрудников
 * (поле service_ids). Права тоже разные: catalog.view и staff.view.
 */
export async function fetchBookingCatalog(signal?: AbortSignal): Promise<BookingCatalog> {
  const [catalog, staff] = await Promise.all([
    apiRequest<{ readonly services?: unknown }>("/cabinet/catalog", { signal }),
    apiRequest<unknown>("/cabinet/catalog/employees", { signal }),
  ])

  const services = Array.isArray(catalog.services) ? catalog.services : []
  const employees = Array.isArray(staff) ? staff : []

  return {
    services: services
      .map(toService)
      .filter((item): item is BookableService => item !== null && item.isActive),
    employees: employees.map(toEmployee).filter((item): item is ServiceEmployee => item !== null),
  }
}
